import { useEffect, useState } from 'react';
import {
  readRegional,
  regionalUrl,
  type RegionalMode,
  type RegionalPage,
} from '../../adapters/regional-http';
import { evidenceSelected } from './weather-selection';
const labels: Record<string, string> = {
  NOT_APPLICABLE: '不适用于正式森林火险判断',
  NOT_APPROVED: '当地 LIVE 规则尚未批准',
  NOT_CONFIGURED: '尚未配置地区边界、授权来源和规则',
  INSUFFICIENT_EVIDENCE: '证据不足，不能判断风险',
  TEMPORAL_UNVERIFIED: '资料在当时是否可得尚无法核实',
  SPACE_MISMATCH: '空间范围不匹配',
  TIME_MISMATCH: '时间或资料可得时点不匹配',
  STALE_DATA: '资料已过期',
  QUALITY_UNVERIFIED: '来源质量尚未证实',
  PROVIDER_UNAVAILABLE: '来源暂不可用',
  SAME_PROVIDER_LINEAGE: 'NWS 两种产品属于同一来源家族',
  HISTORICAL_ONLY: '仅供历史研究',
  NOT_VERIFIED: '热异常尚未核实为火灾',
  NO_QUALIFIED_WEATHER: '冻结研究策略未找到合格区域天气背景，并非当地没有天气',
  FOREST_PROFILE_NOT_ESTABLISHED: '缺少森林植被、燃料、辖区与当地规则依据',
  DISTANT_REGIONAL_CONTEXT: '远距离区域天气背景，非像元现场实况',
  MISSING_COORDINATES: '没有可核实坐标，不绘制位置',
  SOURCE_NOT_AUTHORIZED: '来源不在此地区授权范围内',
};
export function useRegional(mode: RegionalMode | null) {
  const [state, setState] = useState<{
    mode: RegionalMode | null;
    data: RegionalPage | null;
    failed: boolean;
  }>({ mode: null, data: null, failed: false });
  useEffect(() => {
    if (!mode) return;
    const abort = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const load = async () => {
      try {
        const data = await readRegional(mode, abort.signal);
        if (!abort.signal.aborted) setState({ mode, data, failed: false });
      } catch {
        if (!abort.signal.aborted) setState({ mode, data: null, failed: true });
      } finally {
        if (!abort.signal.aborted) timer = setTimeout(() => void load(), 10000);
      }
    };
    void load();
    return () => {
      abort.abort();
      clearTimeout(timer);
    };
  }, [mode]);
  return state.mode === mode ? state : { mode, data: null, failed: false };
}
export function RegionalEvidence({
  mode,
  areaId,
  focus,
  state,
  selectedId,
}: {
  mode: RegionalMode | null;
  areaId: string;
  focus: (lon: number, lat: number, id: string) => void;
  state: ReturnType<typeof useRegional>;
  selectedId: string;
}) {
  const [selected, setSelected] = useState('');
  if (!mode) return null;
  const data = state.mode === mode ? state.data : null;
  const profiles = data?.items ?? [];
  const filter = profiles.some((a) => a.profile.regionId === areaId)
    ? areaId
    : profiles.some((a) => a.profile.regionId === selected)
      ? selected
      : '';
  return (
    <section className="risk-empty" data-vs12b-regional aria-label="地区适用性与证据依据">
      <strong>
        地区适用性 / 证据依据 ·{' '}
        {mode === 'DEMO' ? 'DEMO 演示' : mode === 'LIVE' ? 'LIVE 气象' : '历史研究'}
      </strong>
      <p>后台持续评估；切换地区只筛选查看。未知不等于安全。</p>
      {state.mode === mode && state.failed ? (
        <p role="status">地区证据接口不可用，资格未知；业务 API 状态独立保留。</p>
      ) : !data ? (
        <p>正在读取地区资格…</p>
      ) : (
        <>
          <label>
            授权地区{' '}
            <select
              aria-label="地区证据筛选"
              value={filter}
              onChange={(e) => setSelected(e.target.value)}
            >
              <option value="">全部授权地区</option>
              {profiles.map((a) => (
                <option key={a.profile.regionId} value={a.profile.regionId}>
                  {a.profile.displayName}
                </option>
              ))}
            </select>
          </label>
          {mode === 'HISTORICAL_RESEARCH' && (
            <p data-vs12b-counts>
              冻结历史：{data.historicalCounts.candidates} 候选 / {data.historicalCounts.linked}{' '}
              关联 / {data.historicalCounts.unlinked} 未关联；0 个已核实火灾事件。
            </p>
          )}
          {profiles
            .filter((a) => !filter || a.profile.regionId === filter)
            .map((a) => (
              <details
                key={a.profile.regionId}
                open={a.evidence.some((e) => evidenceSelected(e, selectedId))}
              >
                <summary>
                  {a.profile.displayName} · {labels[a.result] ?? a.result}
                </summary>
                <p>
                  配置状态 {a.profile.status} · {a.explanation}
                </p>
                <p>
                  {labels[a.result] ?? a.result} · {a.evidenceGrade} · {a.approvalState}
                </p>
                <p>
                  实例 {a.profile.instanceId} / 机构范围 {a.profile.agencyId}（本地研究读取权限）
                  <br />
                  真实消防机构 {a.profile.fireAuthority} / 辖区边界{' '}
                  {a.profile.administrationBoundary}
                </p>
                <p>
                  档案 {a.profile.revision} · {a.profile.scenarioApplicability}
                  <br />
                  几何 {a.profile.geometryKind} · {a.profile.geometrySource}
                  <br />
                  {a.profile.spatialResolution}
                </p>
                <p>
                  决策 UTC {a.asOf}
                  <br />
                  地区时间{' '}
                  {new Intl.DateTimeFormat('zh-CN', {
                    timeZone: a.profile.timezone,
                    dateStyle: 'short',
                    timeStyle: 'long',
                  }).format(new Date(a.asOf))}{' '}
                  · {a.profile.timezone}
                </p>
                <p>
                  规则 {a.ruleId} v{a.ruleVersion} / {a.profile.ruleOwner}
                </p>
                <p>
                  合格独立来源家族 {a.independentProviderCount} · {a.corroboration}
                  <br />
                  {a.sourceLineages.join(' / ') || '暂无可核实的独立合格来源'}
                </p>
                <ul>
                  {a.reasonCodes.map((c) => (
                    <li key={c}>
                      {labels[c] ?? c} <small>{c}</small>
                    </li>
                  ))}
                </ul>
                <a
                  href={regionalUrl(mode, a.profile.regionId, a.revision)}
                  target="_blank"
                  rel="noreferrer"
                >
                  固定版本完整证据与资格快照
                </a>
                {a.evidence.map((e) => (
                  <details key={e.id} data-vs12b-evidence open={evidenceSelected(e, selectedId)}>
                    <summary>
                      {e.kind} · {e.sourceId} ·{' '}
                      {e.detail.association ? String(e.detail.association) : e.quality}
                    </summary>
                    <p>
                      来源 {e.provider} / {e.product} · {e.providerFamily}
                      <br />
                      记录 {e.sourceRecordId}
                      <br />
                      修订 {e.revision}
                      <br />
                      SHA256 {e.evidenceDigest}
                    </p>
                    <p>
                      空间 {e.spatialGate} / 时间 {e.temporalGate} / 质量 {e.qualityGate} / 适用{' '}
                      {e.applicabilityGate} / 审批 {e.approvalGate}
                    </p>
                    <p>
                      观测 {e.observedAt ?? '非实测'} · 获取 {e.receivedAt ?? '未知'}
                      <br />
                      签发 {e.issuedAt ?? '不适用'} · 有效 {e.validFrom ?? '见历史采集时间'} →{' '}
                      {e.validUntil ?? '历史研究'}
                    </p>
                    <ul>
                      {e.reasonCodes.map((c) => (
                        <li key={c}>{labels[c] ?? c}</li>
                      ))}
                    </ul>
                    {e.geometry.length > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          const point = e.geometry[0];
                          if (point && typeof point[0] === 'number' && typeof point[1] === 'number')
                            focus(point[0], point[1], e.id);
                        }}
                      >
                        定位证据 · {e.geometryKind}
                      </button>
                    )}
                    <details>
                      <summary>原始关联、距离、时间及质量详情</summary>
                      <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                        {JSON.stringify(e.detail, null, 2)}
                      </pre>
                    </details>
                  </details>
                ))}
              </details>
            ))}
        </>
      )}
    </section>
  );
}
